import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TeamMember, Role, AccessibleVault } from '../../types/team.types';
import { ExclamationCircleIcon, TrashIcon, UserCircleIcon, UserMinusIcon, UsersIcon, XMarkIcon, ChevronDownIcon, ShareIcon, PlusIcon, LockOpenIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useVault } from '../../contexts/VaultContext';
import { TeamService } from '../../services/team.service';
import { useAppSelector } from '../../hooks/useRedux';
import { toast } from 'react-hot-toast';
import { AuthService } from '../../services/auth.service';
import Button from '../common/Button';
import ConfirmModal from '../common/ConfirmModal';
import TransferProgressModal from './TransferProgressModal';
import TransferItemsModal from './TransferItemsModal';
import { ShareService } from '../../services/share.service';
import { hasPermission } from '../../utils/permissionUtils';
import api from '../../services/api';

interface ActiveUsersListProps {
    members: TeamMember[];
    roles?: any[]; // Keep the roles prop optional with a flexible type
    onRoleChange?: (userId: string, newRoleId: number) => void;
    onRevoke: () => void;
    onMemberRemoved?: () => void;
    entityType?: string;
    teamId?: string;
}

const ActiveUsersList: React.FC<ActiveUsersListProps> = ({
    members,
    roles = [], // Default to empty array if not provided
    onRoleChange,
    onRevoke,
    onMemberRemoved,
    entityType = 'Team',
    teamId
}) => {
    const navigate = useNavigate();
    const { searchQuery } = useVault();
    const { permissions, user, role_id, team_id } = useAppSelector((state) => state.auth);
    const hasRemoveMembersPermission = hasPermission(permissions, 'team', 'remove_members');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [selectedUserEmail, setSelectedUserEmail] = useState<string>('');
    const [isRemoving, setIsRemoving] = useState(false);
    const [actionType, setActionType] = useState<'remove' | 'delete' | 'unlock'>('remove');
    const [isGuestUser, setIsGuestUser] = useState(false);
    // New states for identity transfer
    const [transferIdentities, setTransferIdentities] = useState(false);
    const [showTransferProgress, setShowTransferProgress] = useState(false);
    const [transferProgress, setTransferProgress] = useState(0);
    const [transferComplete, setTransferComplete] = useState(false);
    const [transferredCount, setTransferredCount] = useState(0);

    // New state for the transfer items modal
    const [showTransferItemsModal, setShowTransferItemsModal] = useState(false);

    // New states for vault management
    const [showVaultModal, setShowVaultModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
    const [vaultActionType, setVaultActionType] = useState<'remove' | null>(null);
    const [selectedVault, setSelectedVault] = useState<AccessibleVault | null>(null);
    const [availableVaults, setAvailableVaults] = useState<any[]>([]);
    const [isLoadingVaults, setIsLoadingVaults] = useState(false);

    // New state for granting access to other super admins
    const [isGrantingAccess, setIsGrantingAccess] = useState(false);

    // Check if current user is super admin (role_id = 2)
    const isSuperAdmin = role_id === 2;

    const filteredMembers = members.filter((member) => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return member.email?.toLowerCase().includes(searchLower) ||
            member.role_name?.toLowerCase().includes(searchLower);
    });

    const handleDeleteClick = async (userId: string) => {
        setSelectedUserId(userId);
        setActionType('delete');
        setShowConfirmModal(true);
    };

    const handleRemoveMFA = async (userId: string) => {
        try {
            console.log(userId);
            const confirmed = window.confirm('Are you sure you want to remove MFA for this member?');
            if (confirmed) {
                await AuthService.removeMFA(userId);
                await onRevoke();
                toast.success('MFA removed successfully');
            }
        } catch (error) {
            toast.error('Failed to remove MFA');
        }
    };

    const handleViewUserItems = (userId: string, userEmail: string) => {
        navigate(`/admin/user/${userId}/categories`);
    };

    const handleUnlockAccount = async (email: string, userId: string) => {
        setSelectedUserId(userId);
        setSelectedUserEmail(email);
        setActionType('unlock');
        setShowConfirmModal(true);
    };

    const performUnlockAccount = async () => {
        try {
            if (!selectedUserId || !selectedUserEmail) return;

            const result = await AuthService.adminUnlockAccount(selectedUserEmail, selectedUserId);
            
            if (result.success) {
                await onRevoke(); // Refresh the member list
                toast.success('Account unlocked successfully');
            } else {
                toast.error(result.message || 'Failed to unlock account');
            }
        } catch (error) {
            console.error('Error unlocking account:', error);
            toast.error('Failed to unlock account');
        } finally {
            resetState();
        }
    };



    // Grant vault access to another super admin
    const handleGrantAccess = async (targetAdminId: string, targetAdminEmail: string) => {
        if (!window.confirm(`Grant vault access to ${targetAdminEmail}? This will allow them to view all team members' passwords.`)) {
            return;
        }

        try {
            setIsGrantingAccess(true);
            
            // Step 1: Get admin's encrypted private key and authKey from sessionStorage
            const encryptedPrivateKeyStr = sessionStorage.getItem('encryptedPrivateKey');
            const authKey = sessionStorage.getItem('authKey');
            
            if (!encryptedPrivateKeyStr || !authKey) {
                toast.error('Please log out and log in again to grant access.');
                return;
            }
            
            const encryptedPrivateKey = JSON.parse(encryptedPrivateKeyStr);
            
            // Step 2: Decrypt admin's private key
            const { decryptPrivateKey, importPrivateKey, hybridDecrypt } = await import('../../utils/rsaCrypto');
            const adminPrivateKeyBuffer = await decryptPrivateKey(encryptedPrivateKey, authKey);
            const adminPrivateKey = await importPrivateKey(adminPrivateKeyBuffer);
            
            // Step 3: Get admin's encrypted org private key from own team_admin_keys record
            const response = await api.get(`/admin/my-org-key`); // Get own org key

            console.log('Response from /admin/my-org-key:', response.data);
            
            if (!response.data.success) {
                toast.error('Organization key not found. Please contact support.');
                return;
            }
            
            // Step 4: Decrypt org private key using admin's private key
            const encryptedOrgKey = response.data.encryptedOrgPrivateKey;
            const orgPrivateKeyPem = await hybridDecrypt(
                encryptedOrgKey.encryptedData,
                encryptedOrgKey.encryptedKey,
                encryptedOrgKey.iv,
                adminPrivateKey,
                encryptedOrgKey.authTag
            );
            
            // Step 5: Send org private key to backend to wrap for target admin
            const grantResponse = await api.post(`/admin/grant-access/${targetAdminId}`, {
                orgPrivateKeyPem
            });
            console.log('Response from grant access API:', grantResponse.data);
            if (grantResponse.data.success) {
                toast.success(`Vault access granted to ${targetAdminEmail}`);
                // Optionally: trigger a refresh of team members from parent if needed
                onRevoke && onRevoke();
            } else {
                toast.error(grantResponse.data.message || 'Failed to grant access');
            }
            
        } catch (error: any) {
            console.error('Error granting access:', error);
            toast.error('Failed to grant access: ' + error.message);
        } finally {
            setIsGrantingAccess(false);
        }
    };

    const handleRemoveMember = async (memberId: string, isGuestUser: boolean = false) => {
        console.log(memberId);
        setIsGuestUser(isGuestUser);
        setSelectedUserId(memberId);
        setActionType('remove');
        setShowConfirmModal(true);
    };

    const confirmRemove = async () => {
        if (!selectedUserId) return;

        try {
            setIsRemoving(true);
            console.log('teamId', teamId);
            if (!teamId) {
                teamId = team_id || '';
            }

            // If transfer option is selected, show the transfer items modal instead of immediate transfer
            if (transferIdentities) {
                setShowConfirmModal(false);
                setShowTransferItemsModal(true);
                return;
            }

            // If no transfer needed, just perform the action
            if (actionType === 'remove') {
                await removeMemberFromTeam();
            } else if (actionType === 'delete') {
                await deleteUser();
            } else if (actionType === 'unlock') {
                await performUnlockAccount();
            }
        } catch (error) {
            const errorAction = actionType === 'remove' ? 'removing' : actionType === 'delete' ? 'deleting' : 'unlocking';
            const errorMessage = actionType === 'remove' ? 'remove' : actionType === 'delete' ? 'delete' : 'unlock';
            console.error(`Error ${errorAction} member:`, error);
            toast.error(`Failed to ${errorMessage} ${actionType === 'unlock' ? 'account' : 'member'}`);
            setIsRemoving(false);
            setShowConfirmModal(false);
            setShowTransferProgress(false);
            setSelectedUserId(null);
        }
    };

    const removeMemberFromTeam = async () => {
        try {
            if (!selectedUserId) return;

            // Find the member to get their ID
            const member = members.find(m => m.user_id === selectedUserId);
            if (member) {
                await TeamService.removeMemberFromTeam(selectedUserId, teamId || team_id || '', isGuestUser || false);
                toast.success('Member removed successfully');
                onRevoke();
                if (onMemberRemoved) onMemberRemoved();
            }
        } catch (error) {
            console.error('Error removing member:', error);
            toast.error('Failed to remove member');
        } finally {
            resetState();
        }
    };

    const deleteUser = async () => {
        try {
            if (!selectedUserId) return;

            await TeamService.deleteUser(selectedUserId);
            toast.success('User and all associated data permanently removed');
            onRevoke();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        } finally {
            resetState();
        }
    };

    const resetState = () => {
        setIsRemoving(false);
        setShowConfirmModal(false);
        setShowTransferProgress(false);
        setSelectedUserId(null);
        setSelectedUserEmail('');
        setTransferIdentities(false);
        setTransferProgress(0);
        setTransferComplete(false);
        setActionType('remove');
    };

    // New function to handle removing vault access
    const handleRemoveVaultAccess = async (vault: AccessibleVault, member: TeamMember) => {
        setSelectedVault(vault);
        setSelectedMember(member);
        setVaultActionType('remove');
        setShowVaultModal(true);
    };

    // Function to handle vault action confirmation
    const handleVaultAction = async () => {
        if (!selectedVault || !selectedMember || !vaultActionType) return;

        try {
            if (vaultActionType === 'remove') {
                await ShareService.revokeItemAccess(
                    selectedMember.user_id || '',
                    selectedVault.id,
                    'user'
                );
                toast.success(`Access to "${selectedVault.name}" has been revoked`);
                onRevoke(); // Refresh the member list
            }
        } catch (error) {
            console.error('Error managing vault access:', error);
            toast.error('Failed to manage vault access');
        } finally {
            resetVaultModalState();
        }
    };

    // Helper function to reset vault modal state
    const resetVaultModalState = () => {
        setShowVaultModal(false);
        setSelectedVault(null);
        setSelectedMember(null);
        setVaultActionType(null);
    };

    // Handle transfer completion
    const handleTransferComplete = (count: number) => {
        setTransferredCount(count);
        setTransferComplete(true);
        setShowTransferItemsModal(false);

        // Show transfer progress modal with 100% completion
        setTransferProgress(100);
        setShowTransferProgress(true);

        // After a delay, proceed with the user action
        setTimeout(() => {
            if (actionType === 'remove') {
                removeMemberFromTeam();
            } else {
                deleteUser();
            }
        }, 1000);
    };

    // Add this enhanced component to display vaults in a collapsible section with management options
    const UserVaultsSection: React.FC<{ vaults: AccessibleVault[], member: TeamMember }> = ({ vaults, member }) => {
        const [isExpanded, setIsExpanded] = useState(false);

        // Group vaults by ownership status
        const ownedVaults = vaults.filter(v => v.is_owned);
        const sharedVaults = vaults.filter(v => !v.is_owned && !v.shared_with_team);
        const teamSharedVaults = vaults.filter(v => !v.is_owned && v.shared_with_team);

        // Get total count
        const totalVaults = vaults.length;

        return (
            <div className="mt-3 space-y-2">
                {/* Vault Summary Bar */}
                <div className="bg-gray-50 rounded-lg px-2 sm:px-3 py-2 hover:bg-gray-100 transition-colors">
                    <button
                        className="flex items-center justify-between w-full text-sm text-gray-700 hover:text-gray-900 focus:outline-none"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <div className="flex items-center space-x-1 sm:space-x-2">
                            <ChevronDownIcon
                                className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                            <span className="font-medium text-xs sm:text-sm">Vault Access</span>
                        </div>

                        {/* Vault Count Badges - Responsive */}
                        <div className="flex items-center space-x-1 ml-1 sm:ml-2">
                            {ownedVaults.length > 0 && (
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                    <span className="hidden xs:inline">{ownedVaults.length} </span>
                                    <span className="hidden sm:inline">Owned</span>
                                    <span className="sm:hidden">O</span>
                                </span>
                            )}
                            {sharedVaults.length > 0 && (
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    <span className="hidden xs:inline">{sharedVaults.length} </span>
                                    <span className="hidden sm:inline">Shared</span>
                                    <span className="sm:hidden">S</span>
                                </span>
                            )}
                            {teamSharedVaults.length > 0 && (
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                    <span className="hidden xs:inline">{teamSharedVaults.length} </span>
                                    <span className="hidden sm:inline">Team</span>
                                    <span className="sm:hidden">T</span>
                                </span>
                            )}
                        </div>
                    </button>
                </div>

                {/* Expanded Vault Details */}
                {isExpanded && (
                    <div className="pl-3 space-y-4">
                        {/* Owned Vaults Section - Keep horizontal design */}
                        {ownedVaults.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-xs font-semibold text-green-600 uppercase tracking-wider">Owned Vaults ({ownedVaults.length})</h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {ownedVaults.map(vault => (
                                        <span
                                            key={vault.id}
                                            className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
                                            title={`Owned: ${vault.name}`}
                                        >
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
                                            {vault.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Shared Vaults Section - Revert to old design */}
                        {sharedVaults.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Direct Access</h4>
                                <div className="space-y-1">
                                    {sharedVaults.map(vault => (
                                        <div
                                            key={vault.id}
                                            className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 group relative"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                                <span className="text-sm text-gray-700">{vault.name}</span>
                                                {vault.shared_by && (
                                                    <span className="text-xs text-gray-500">from {vault.shared_by}</span>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex items-center space-x-2">
                                                {hasRemoveMembersPermission && (
                                                    <button
                                                        onClick={() => handleRemoveVaultAccess(vault, member)}
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-red-50 text-red-500 hover:text-red-700"
                                                        title="Remove vault access"
                                                    >
                                                        <XMarkIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Team Shared Vaults Section - Revert to old design */}
                        {teamSharedVaults.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Team Access</h4>
                                <div className="space-y-1">
                                    {teamSharedVaults.map(vault => (
                                        <div
                                            key={vault.id}
                                            className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 group"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                                <span className="text-sm text-gray-700">{vault.name}</span>
                                                {vault.shared_by && (
                                                    <span className="text-xs text-gray-500">via team from {vault.shared_by}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {totalVaults === 0 && (
                            <div className="text-center py-4">
                                <div className="text-sm text-gray-500">No vault access</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (members.length === 0) {
        return (
            <div className="text-center py-16 px-6">
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                          rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <UsersIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No members found</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                    Get started by inviting members to your {entityType.toLowerCase()}.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 shadow-xl overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
            {filteredMembers.length === 0 ? (
                <div className="flex items-center justify-center p-8 text-gray-500 dark:text-gray-400">
                    <ExclamationCircleIcon className="h-6 w-6 mr-2" />
                    <span>No active members found.</span>
                </div>
            ) : (
                <>
                    {/* Desktop view */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Member</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Joined</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">V2 Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Role</th>
                                    {hasRemoveMembersPermission && (
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredMembers.map((member) => (
                                    <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                                        <td className="px-6 py-5">
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 w-10 h-10">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                                                        <span className="text-sm font-semibold text-white">
                                                            {member.email?.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="ml-4 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.email}</div>
                                                        {member.user_status === 'locked' && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                                🔒 Locked
                                                            </span>
                                                        )}
                                                    </div>
                                                    {team_id && member.team_id != team_id && (
                                                        <div className="flex items-center text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-medium">
                                                                Guest User
                                                            </span>
                                                        </div>
                                                    )}
                                                    {member.team_name && window.location.pathname === '/manage-members' && (role_id === 1 || role_id === 2) && (
                                                        <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 mt-1">Team - {member.team_name}</div>
                                                    )}
                                                    {role_id !== 1 && (
                                                        <UserVaultsSection vaults={member.accessible_vaults || []} member={member} />
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 align-top">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                                {new Date(member.joined_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 align-top">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                                member.crypto_version === 2 
                                                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                                                    : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                            }`}>
                                                {member.crypto_version === 2 ? '✓ Upgraded' : '⚠ Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 align-top">
                                            {hasPermission(permissions, 'role_management', 'assign_roles') && onRoleChange && roles && roles.length > 0  && window.location.pathname === '/manage-members'? (
                                                <select
                                                    value={member.role_id}
                                                    onChange={(e) => onRoleChange?.(member.user_id || member.id, Number(e.target.value))}
                                                    className="text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:text-gray-200"
                                                    disabled={member.user_id === user?.id}
                                                    title={member.user_id === user?.id ? 'You cannot change your own role' : ''}
                                                >
                                                    <option value={0}>Select Role</option>
                                                    {roles?.map((role) => (
                                                        <option key={role.id} value={role.id}>
                                                            {role.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
                                                    {member.role_name}
                                                </span>
                                            )}
                                        </td>
                                        {(hasRemoveMembersPermission) && (
                                            <>
                                                {member.team_id === team_id ?

                                                    <td className="px-6 py-5 align-top">
                                                        <div className="flex flex-wrap gap-2">
                                                            {window.location.pathname !== '/manage-members' && role_id !== 1 && (
                                                                <button
                                                                    onClick={() => handleRemoveMember(member.user_id || member.id)}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 hover:text-orange-800 rounded-lg transition-colors text-sm font-medium"
                                                                >
                                                                    <UserMinusIcon className="h-4 w-4 mr-1" />
                                                                    Remove
                                                                </button>
                                                            )}
                                                            {window.location.pathname == '/manage-members' && (
                                                                <button
                                                                    onClick={() => handleDeleteClick(member.user_id || member.id)}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    disabled={
                                                                        member.user_id === user?.id || // can't delete self
                                                                        (
                                                                            member.role_id === 2 &&     // member is privileged
                                                                            !(role_id === 1 || role_id === 2) // current user is not allowed
                                                                        )
                                                                    }
                                                                    title={
                                                                        member.user_id === user?.id
                                                                            ? 'You cannot delete yourself'
                                                                            : (member.role_id === 2 && !(role_id === 1 || role_id === 2))
                                                                            ? 'You do not have permission to delete this user'
                                                                            : ''
                                                                    }
                                                                >
                                                                    <TrashIcon className="h-4 w-4 mr-1" />
                                                                    Delete
                                                                </button>
                                                            )}

                                                            {/* Remove MFA button */}
                                                            {member?.two_factor_enabled && (
                                                                <button
                                                                    onClick={() => handleRemoveMFA(member.user_id || member.id)}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 hover:text-blue-800 rounded-lg transition-colors text-sm font-medium"
                                                                >
                                                                    Remove MFA
                                                                </button>
                                                            )}

                                                            {/* Unlock Account button */}
                                                            {member?.user_status === 'locked' && (
                                                                <button
                                                                    onClick={() => handleUnlockAccount(member.email, member.user_id || member.id)}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 hover:text-green-800 rounded-lg transition-colors text-sm font-medium"
                                                                >
                                                                    <LockOpenIcon className="h-4 w-4 mr-1" />
                                                                    Unlock Account
                                                                </button>
                                                            )}

                                                            {/* View Items button (Super Admin only) */}
                                                            {isSuperAdmin && member.user_id !== user?.id && (
                                                                <button
                                                                    onClick={() => handleViewUserItems(member.user_id || member.id, member.email)}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 hover:text-purple-800 rounded-lg transition-colors text-sm font-medium"
                                                                    title="View all items (passwords, notes, cards, etc.)"
                                                                >
                                                                    <EyeIcon className="h-4 w-4 mr-1" />
                                                                    View Items
                                                                </button>
                                                            )}

                                                            {/* Grant Access button (Super Admin only, for other super admins without access) */}
                                                            {isSuperAdmin && 
                                                             member.role_id === 2 && 
                                                             member.user_id !== user?.id && 
                                                             !member.has_admin_access && (
                                                                <button
                                                                    onClick={() => handleGrantAccess(member.user_id || member.id, member.email)}
                                                                    disabled={isGrantingAccess}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 hover:text-blue-800 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    title="Grant vault access to this super admin"
                                                                >
                                                                    <ShareIcon className="h-4 w-4 mr-1" />
                                                                    {isGrantingAccess ? 'Granting...' : 'Grant Vault Access'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    :
                                                    <td className="px-6 py-5 align-top">
                                                        <div className="flex flex-wrap gap-2">
                                                            {role_id === 1 ? (
                                                                <button
                                                                    onClick={() => handleDeleteClick(member.user_id || member.id)}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    disabled={
                                                                        member.user_id === user?.id || // can't delete self
                                                                        (
                                                                            member.role_id === 2 &&     // member is privileged
                                                                            !(role_id === 1 || role_id === 2) // current user is not allowed
                                                                        )
                                                                    }
                                                                    title={
                                                                        member.user_id === user?.id
                                                                            ? 'You cannot delete yourself'
                                                                            : (member.role_id === 2 && !(role_id === 1 || role_id === 2))
                                                                            ? 'You do not have permission to delete this user'
                                                                            : ''
                                                                    }
                                                                >
                                                                    <TrashIcon className="h-4 w-4 mr-1" />
                                                                    Delete
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleRemoveMember(member.user_id || member.id, true)}
                                                                    className="inline-flex items-center px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 hover:text-orange-800 rounded-lg transition-colors text-sm font-medium"
                                                                >
                                                                    <UserMinusIcon className="h-4 w-4 mr-1" />
                                                                    Remove
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                }

                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile view */}
                    <div className="block md:hidden">
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredMembers.map((member) => (
                                <div key={member.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0 w-10 h-10">
                                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-semibold text-white">
                                                    {member.email?.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{member.email}</p>
                                                        {member.user_status === 'locked' && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                                🔒
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 font-medium">
                                                            {member.role_name}
                                                        </span>
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full font-medium ${
                                                            member.crypto_version === 2 
                                                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                                                                : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                                        }`}>
                                                            {member.crypto_version === 2 ? 'V2 ✓' : 'V1 ⚠'}
                                                        </span>
                                                        {team_id && member.team_id != team_id && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-medium">
                                                                Guest User
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                        Joined {new Date(member.joined_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    </p>
                                                </div>
                                                {hasRemoveMembersPermission && (
                                                    <div className="flex space-x-2">
                                                        {member.user_status === 'locked' && (
                                                            <button
                                                                onClick={() => handleUnlockAccount(member.email, member.user_id || member.id)}
                                                                className="text-green-600 hover:text-green-800 focus:outline-none"
                                                                title="Unlock Account"
                                                            >
                                                                <LockOpenIcon className="h-5 w-5" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleRemoveMember(member.user_id)}
                                                            className="text-orange-600 hover:text-orange-800 focus:outline-none"
                                                            title="Remove Member from Team"
                                                        >
                                                            <UserMinusIcon className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(member.user_id || member.id)}
                                                            className="text-red-600 hover:text-red-800 focus:outline-none"
                                                            title="Delete Member Permanently"
                                                        >
                                                            <TrashIcon className="h-5 w-5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Joined: {new Date(member.joined_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </p>
                                            <div className="mt-2">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                                                {hasPermission(permissions, 'role_management', 'assign_roles') && onRoleChange && roles && roles.length > 0 ? (
                                                    <select
                                                        value={member.role_id || ''}
                                                        onChange={(e) => onRoleChange?.(member.user_id, Number(e.target.value))}
                                                        className="w-full text-sm mt-2 border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                                    >
                                                        <option value="">Select Role</option>
                                                        {roles?.map((role) => (
                                                            <option key={role.id} value={role.id}>
                                                                {role.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div className="text-sm text-gray-700">
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                            {member.role_name}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            {role_id !== 1 && (
                                                <UserVaultsSection vaults={member.accessible_vaults || []} member={member} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Confirm Modal with Transfer Option */}
            {showConfirmModal && (
                <ConfirmModal
                    isOpen={true}
                    title={
                        actionType === 'remove' 
                            ? "Remove Team Member" 
                            : actionType === 'delete' 
                            ? "Delete User" 
                            : "Unlock Account"
                    }
                    message={
                        actionType === 'remove'
                            ? `Are you sure you want to remove this member from the team?`
                            : actionType === 'delete'
                            ? "Are you sure you want to delete this user? This will permanently remove the user and all their data."
                            : `Are you sure you want to unlock the account for ${selectedUserEmail}? This will restore access to their account.`
                    }
                    confirmText={
                        actionType === 'remove' 
                            ? "Remove" 
                            : actionType === 'delete' 
                            ? "Delete" 
                            : "Unlock Account"
                    }
                    cancelButtonText="Cancel"
                    isLoading={isRemoving}
                    onConfirm={confirmRemove}
                    onCancel={() => {
                        setShowConfirmModal(false);
                        setSelectedUserId(null);
                        setSelectedUserEmail('');
                        setTransferIdentities(false);
                    }}
                >

                    {/* Only show transfer option for remove/delete actions, not unlock */}
                    {actionType !== 'unlock' && (
                        <div className="mt-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={transferIdentities}
                                    onChange={(e) => setTransferIdentities(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                    Transfer this member's shared items to me
                                </span>
                            </label>
                            <p className="mt-1 text-xs text-gray-500">
                                This will transfer all identities shared with the team by this member to your account.
                            </p>
                        </div>
                    )}
                </ConfirmModal>
            )}

            {/* Transfer Items Modal */}
            {showTransferItemsModal && selectedUserId && (
                <TransferItemsModal
                    isOpen={showTransferItemsModal}
                    memberId={selectedUserId}
                    teamId={teamId || team_id || undefined}
                    onClose={() => {
                        setShowTransferItemsModal(false);
                        setIsRemoving(false);
                        setSelectedUserId(null);
                    }}
                    onTransferComplete={handleTransferComplete}
                />
            )}

            {/* Transfer Progress Modal */}
            {showTransferProgress && (
                <TransferProgressModal
                    isOpen={showTransferProgress}
                    progress={transferProgress}
                    isComplete={transferComplete}
                    itemCount={transferredCount}
                    onClose={() => {
                        if (transferComplete) {
                            setShowTransferProgress(false);
                        }
                    }}
                />
            )}

            {/* Vault Action Confirmation Modal */}
            {showVaultModal && selectedVault && selectedMember && (
                <ConfirmModal
                    isOpen={true}
                    title="Remove Vault Access"
                    message={`Are you sure you want to revoke ${selectedMember.email}'s access to "${selectedVault.name}"?`}
                    confirmText="Remove Access"
                    cancelButtonText="Cancel"
                    isLoading={isRemoving}
                    onConfirm={handleVaultAction}
                    onCancel={resetVaultModalState}
                />
            )}
        </div>
    );
};

export default ActiveUsersList;
